// ===== frontend/src/pages/DemoPage.jsx =====
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const DemoPage = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [demoData, setDemoData] = useState({});
  const [showAIPanel, setShowAIPanel] = useState(false);

  const steps = [
    { id: 'intro', title: 'Welcome to FormBridge Demo', description: 'See how we transform complex PDFs into accessible web forms' },
    { id: 'pdf-preview', title: 'PDF Preview', description: 'First, let\'s look at a typical government form' },
    { id: 'extraction', title: 'Field Extraction', description: 'Our AI analyzes and extracts form fields' },
    { id: 'translation', title: 'Translation', description: 'German labels are automatically translated to English' },
    { id: 'form-demo', title: 'Interactive Form', description: 'Experience the user-friendly web interface' },
    { id: 'ai-fill', title: 'AI Assistance', description: 'Watch AI automatically fill the form' },
    { id: 'download', title: 'Download Result', description: 'Generate and download the completed PDF' }
  ];

  const sampleFormStructure = {
    title: 'City Registration Form (Anmeldung)',
    description: 'Register your new residence with the city administration',
    sections: [
      {
        sectionId: 'residence_type',
        title: 'Type of Residence',
        fields: [
          { 
            id: 'residenceType', 
            label: 'The new residence is', 
            type: 'checkbox', 
            required: true,
            options: [
              { value: 'sole', label: 'Sole residence (alleinige Wohnung)' },
              { value: 'main', label: 'Main residence (Hauptwohnung)' },
              { value: 'secondary', label: 'Secondary residence (Nebenwohnung)' }
            ]
          }
        ]
      },
      {
        sectionId: 'move_details',
        title: 'Move-in Details',
        fields: [
          { id: 'moveInDate', label: 'Date of Move-in (Tag des Einzugs)', type: 'text', placeholder: 'DD.MM.YYYY', required: true },
          { id: 'municipality', label: 'Municipality (Gemeinde)', type: 'text', required: true },
          { id: 'postalCode', label: 'Postal Code (Postleitzahl)', type: 'text', required: true }
        ]
      },
      {
        sectionId: 'address',
        title: 'Address Information',
        fields: [
          { id: 'street', label: 'Street, House Number, Additions (Straße, Hausnummer, Zusätze)', type: 'text', required: true, placeholder: 'e.g., Königsallee 25a' }
        ]
      },
      {
        sectionId: 'personal',
        title: 'Personal Information',
        fields: [
          { id: 'familyName', label: 'Family Name (Familienname)', type: 'text', required: true },
          { id: 'firstName', label: 'First Name (Vorname)', type: 'text', required: true },
          { id: 'dateOfBirth', label: 'Date of Birth (Tag der Geburt)', type: 'text', placeholder: 'DD.MM.YYYY', required: true },
          { id: 'placeOfBirth', label: 'Place and Country of Birth (Geburtsort, Land)', type: 'text', placeholder: 'e.g., Hamburg, Germany' }
        ]
      }
    ]
  };

  const aiSampleData = {
    residenceType: 'main',
    moveInDate: '15.03.2024',
    municipality: 'Düsseldorf',
    postalCode: '40212',
    street: 'Königsallee 25a',
    familyName: 'Smith',
    firstName: 'John',
    dateOfBirth: '15.05.1990',
    placeOfBirth: 'New York, USA'
  };

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentStep(currentStep + 1);
        setIsAnimating(false);
      }, 300);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentStep(currentStep - 1);
        setIsAnimating(false);
      }, 300);
    }
  };

  const handleAIFill = () => {
    setShowAIPanel(true);
    // Simulate AI processing
    setTimeout(() => {
      setShowAIPanel(false);
      // Animate form filling
      Object.entries(aiSampleData).forEach(([key, value], index) => {
        setTimeout(() => {
          setDemoData(prev => ({ ...prev, [key]: value }));
        }, index * 200);
      });
    }, 2000);
  };

  const renderStepContent = () => {
    switch (steps[currentStep].id) {
      case 'intro':
        return (
          <div style={{ textAlign: 'center', padding: '3rem 0' }}>
            <div style={{
              width: '5rem',
              height: '5rem',
              backgroundColor: '#dbeafe',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 2rem auto'
            }}>
              <svg style={{ width: '2.5rem', height: '2.5rem', color: '#2563eb' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h2 style={{ fontSize: '2rem', fontWeight: 'bold', color: '#111827', marginBottom: '1rem' }}>
              Transform PDFs into Accessible Forms
            </h2>
            <p style={{ fontSize: '1.125rem', color: '#6b7280', marginBottom: '2rem', maxWidth: '32rem', margin: '0 auto 2rem auto' }}>
              Watch how FormBridge converts a complex German registration form into a user-friendly web interface with AI assistance.
            </p>
            <button onClick={nextStep} className="btn-primary">
              Start Demo
            </button>
          </div>
        );

      case 'pdf-preview':
        return (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', alignItems: 'center' }}>
            <div>
              <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#111827', marginBottom: '1rem' }}>
                Original PDF Form
              </h3>
              <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>
                This is a typical German city registration form. Notice the complex layout, German text, and scattered field positions.
              </p>
              <div style={{ backgroundColor: '#fef3c7', border: '1px solid #f59e0b', borderRadius: '0.5rem', padding: '1rem' }}>
                <h4 style={{ fontSize: '0.875rem', fontWeight: '600', color: '#92400e', marginBottom: '0.5rem' }}>
                  Common PDF Issues:
                </h4>
                <ul style={{ fontSize: '0.875rem', color: '#b45309', margin: 0, paddingLeft: '1rem' }}>
                  <li>Non-accessible for screen readers</li>
                  <li>Poor mobile experience</li>
                  <li>Confusing field order</li>
                  <li>No input validation</li>
                  <li>German language barrier</li>
                </ul>
              </div>
            </div>
            
            {/* Actual PDF Image */}
            <div style={{
              position: 'relative',
              backgroundColor: 'white',
              borderRadius: '0.5rem',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              overflow: 'hidden'
            }}>
              <img
                src="/pdf-sample.png"
                alt="German city registration form showing complex layout with scattered fields and German labels"
                style={{
                  width: '100%',
                  height: 'auto',
                  display: 'block',
                  border: '2px solid #e5e7eb'
                }}
                onError={(e) => {
                  // Fallback if image doesn't exist
                  e.target.style.display = 'none';
                  e.target.nextElementSibling.style.display = 'block';
                }}
              />
              
              {/* Fallback content if image fails to load */}
              <div style={{
                display: 'none',
                padding: '3rem 1.5rem',
                textAlign: 'center',
                color: '#6b7280',
                border: '2px dashed #d1d5db'
              }}>
                <svg style={{ width: '3rem', height: '3rem', margin: '0 auto 1rem', color: '#9ca3af' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p style={{ fontSize: '0.875rem', margin: 0 }}>
                  Sample German Registration Form<br/>
                  <span style={{ fontSize: '0.75rem' }}>Complex PDF with scattered fields</span>
                </p>
              </div>
              
              {/* PDF Badge */}
              <div style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                backgroundColor: '#dc2626',
                color: 'white',
                padding: '0.5rem 0.75rem',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                fontWeight: 'bold',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
              }}>
                PDF
              </div>
              
              {/* Optional: Highlight problem areas */}
              <div style={{
                position: 'absolute',
                bottom: '1rem',
                left: '1rem',
                right: '1rem',
                backgroundColor: 'rgba(239, 68, 68, 0.9)',
                color: 'white',
                padding: '0.75rem',
                borderRadius: '0.375rem',
                fontSize: '0.75rem',
                textAlign: 'center'
              }}>
                ⚠️ Not mobile-friendly • Hard to read • No validation
              </div>
            </div>
          </div>
        );

      case 'extraction':
        return (
          <div style={{ textAlign: 'center' }}>
            <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#111827', marginBottom: '1rem' }}>
              AI Field Extraction
            </h3>
            <p style={{ color: '#6b7280', marginBottom: '2rem' }}>
              Our AI analyzes the PDF and identifies all form fields, their types, and relationships.
            </p>
            
            {/* Extraction Animation */}
            <div style={{ position: 'relative', backgroundColor: '#f8fafc', borderRadius: '0.75rem', padding: '2rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '2rem', alignItems: 'center' }}>
                {/* PDF Side */}
                <div style={{
                  backgroundColor: 'white',
                  border: '2px solid #e5e7eb',
                  borderRadius: '0.5rem',
                  padding: '1rem',
                  position: 'relative'
                }}>
                  <h4 style={{ fontSize: '0.875rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>PDF Fields</h4>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                    ■ Vorname (Text)<br/>
                    ■ Nachname (Text)<br/>
                    ■ Straße (Text)<br/>
                    ■ PLZ (Number)
                  </div>
                </div>
                
                {/* Arrow with Processing */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
                  <div style={{
                    width: '2rem',
                    height: '2rem',
                    border: '2px solid #8b5cf6',
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                  <span style={{ fontSize: '0.75rem', color: '#8b5cf6', fontWeight: '500' }}>Processing</span>
                </div>
                
                {/* Structured Data Side */}
                <div style={{
                  backgroundColor: 'white',
                  border: '2px solid #10b981',
                  borderRadius: '0.5rem',
                  padding: '1rem'
                }}>
                  <h4 style={{ fontSize: '0.875rem', fontWeight: 'bold', marginBottom: '0.5rem', color: '#10b981' }}>Structured Data</h4>
                  <div style={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>
                    {`{
  "firstName": "text",
  "lastName": "text", 
  "street": "text",
  "postalCode": "text"
}`}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'translation':
        return (
          <div style={{ textAlign: 'center' }}>
            <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#111827', marginBottom: '1rem' }}>
              Automatic Translation
            </h3>
            <p style={{ color: '#6b7280', marginBottom: '2rem' }}>
              German labels are automatically translated to English for better accessibility.
            </p>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', maxWidth: '48rem', margin: '0 auto' }}>
              {/* Before */}
              <div style={{
                backgroundColor: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '0.5rem',
                padding: '1.5rem'
              }}>
                <h4 style={{ fontSize: '1rem', fontWeight: 'bold', color: '#991b1b', marginBottom: '1rem' }}>Before (German)</h4>
                <div style={{ textAlign: 'left', fontSize: '0.875rem', color: '#7f1d1d', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <span>Vorname</span>
                  <span>Nachname</span>
                  <span>Straße und Hausnummer</span>
                  <span>Postleitzahl</span>
                  <span>Wohnort</span>
                </div>
              </div>
              
              {/* After */}
              <div style={{
                backgroundColor: '#f0fdf4',
                border: '1px solid #bbf7d0',
                borderRadius: '0.5rem',
                padding: '1.5rem'
              }}>
                <h4 style={{ fontSize: '1rem', fontWeight: 'bold', color: '#166534', marginBottom: '1rem' }}>After (English)</h4>
                <div style={{ textAlign: 'left', fontSize: '0.875rem', color: '#14532d', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <span>First Name</span>
                  <span>Last Name</span>
                  <span>Street Address</span>
                  <span>Postal Code</span>
                  <span>City</span>
                </div>
              </div>
            </div>
          </div>
        );

      case 'form-demo':
        return (
          <div>
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#111827', marginBottom: '1rem' }}>
                Interactive Web Form
              </h3>
              <p style={{ color: '#6b7280' }}>
                Try filling out the form below. Notice the improved accessibility and user experience.
              </p>
            </div>
            
            {/* Interactive Form Demo */}
            <div style={{ maxWidth: '32rem', margin: '0 auto' }}>
              {sampleFormStructure.sections.map((section) => (
                <div key={section.sectionId} style={{
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '0.75rem',
                  marginBottom: '1.5rem',
                  overflow: 'hidden'
                }}>
                  <div style={{ padding: '1rem', borderBottom: '1px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
                    <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#111827', margin: 0 }}>
                      {section.title}
                    </h4>
                  </div>
                  <div style={{ padding: '1.5rem' }}>
                    {section.fields.map((field) => (
                      <div key={field.id} style={{ marginBottom: '1rem' }}>
                        <label style={{
                          display: 'block',
                          fontSize: '0.875rem',
                          fontWeight: '500',
                          color: '#374151',
                          marginBottom: '0.5rem'
                        }}>
                          {field.label}
                          {field.required && <span style={{ color: '#ef4444', marginLeft: '0.25rem' }}>*</span>}
                        </label>
                        
                        {field.type === 'checkbox' && field.options ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            {field.options.map((option, index) => (
                              <label key={index} style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                fontSize: '0.875rem',
                                cursor: 'pointer'
                              }}>
                                <input
                                  type="radio"
                                  name={field.id}
                                  value={option.value}
                                  checked={demoData[field.id] === option.value}
                                  onChange={(e) => setDemoData(prev => ({ ...prev, [field.id]: e.target.value }))}
                                  style={{ accentColor: '#2563eb' }}
                                />
                                {option.label}
                              </label>
                            ))}
                          </div>
                        ) : (
                          <input
                            type={field.type}
                            value={demoData[field.id] || ''}
                            onChange={(e) => setDemoData(prev => ({ ...prev, [field.id]: e.target.value }))}
                            placeholder={field.placeholder}
                            style={{
                              width: '100%',
                              padding: '0.75rem',
                              border: '1px solid #d1d5db',
                              borderRadius: '0.5rem',
                              fontSize: '0.875rem',
                              transition: 'border-color 0.2s'
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'ai-fill':
        return (
          <div>
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#111827', marginBottom: '1rem' }}>
                AI-Powered Form Filling
              </h3>
              <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>
                Watch how AI can intelligently fill forms based on provided information.
              </p>
              <button
                onClick={handleAIFill}
                disabled={showAIPanel}
                style={{
                  backgroundColor: showAIPanel ? '#e5e7eb' : '#8b5cf6',
                  color: showAIPanel ? '#9ca3af' : 'white',
                  padding: '0.75rem 1.5rem',
                  border: 'none',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  cursor: showAIPanel ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  margin: '0 auto'
                }}
              >
                {showAIPanel ? (
                  <>
                    <div style={{
                      width: '1rem',
                      height: '1rem',
                      border: '2px solid #9ca3af',
                      borderTop: '2px solid transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <svg style={{ width: '1rem', height: '1rem' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    Try AI Fill
                  </>
                )}
              </button>
            </div>

            {/* Sample Input Data */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', marginBottom: '2rem' }}>
              <div style={{
                backgroundColor: '#eff6ff',
                border: '1px solid #bfdbfe',
                borderRadius: '0.5rem',
                padding: '1.5rem'
              }}>
                <h4 style={{ fontSize: '1rem', fontWeight: 'bold', color: '#1e40af', marginBottom: '1rem' }}>
                  Sample Input Data
                </h4>
                <div style={{ fontSize: '0.875rem', color: '#1d4ed8', fontFamily: 'monospace', whiteSpace: 'pre-line' }}>
                  {`Name: John Smith
Date of Birth: 15.05.1990
Place of Birth: New York, USA
Move-in Date: 15.03.2024
Address: Königsallee 25a
Postal Code: 40212
Municipality: Düsseldorf
Residence Type: Main residence`}
                </div>
              </div>
              
              <div style={{
                backgroundColor: '#f0fdf4',
                border: '1px solid #bbf7d0',
                borderRadius: '0.5rem',
                padding: '1.5rem'
              }}>
                <h4 style={{ fontSize: '1rem', fontWeight: 'bold', color: '#166534', marginBottom: '1rem' }}>
                  AI Processing
                </h4>
                <div style={{ fontSize: '0.875rem', color: '#14532d' }}>
                  ✓ Parse personal information<br/>
                  ✓ Extract address components<br/>
                  ✓ Match fields intelligently<br/>
                  ✓ Validate data format
                </div>
              </div>
            </div>

            {/* Form with AI-filled data */}
            <div style={{ maxWidth: '32rem', margin: '0 auto' }}>
              {sampleFormStructure.sections.map((section) => (
                <div key={section.sectionId} style={{
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '0.75rem',
                  marginBottom: '1.5rem',
                  overflow: 'hidden'
                }}>
                  <div style={{ padding: '1rem', borderBottom: '1px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
                    <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#111827', margin: 0 }}>
                      {section.title}
                    </h4>
                  </div>
                  <div style={{ padding: '1.5rem' }}>
                    {section.fields.map((field) => (
                      <div key={field.id} style={{ marginBottom: '1rem' }}>
                        <label style={{
                          display: 'block',
                          fontSize: '0.875rem',
                          fontWeight: '500',
                          color: '#374151',
                          marginBottom: '0.5rem'
                        }}>
                          {field.label}
                          {field.required && <span style={{ color: '#ef4444', marginLeft: '0.25rem' }}>*</span>}
                        </label>
                        
                        {field.type === 'checkbox' && field.options ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            {field.options.map((option, index) => (
                              <label key={index} style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                fontSize: '0.875rem',
                                cursor: 'pointer',
                                opacity: demoData[field.id] === option.value ? 1 : 0.6
                              }}>
                                <input
                                  type="radio"
                                  name={field.id}
                                  value={option.value}
                                  checked={demoData[field.id] === option.value}
                                  readOnly
                                  style={{ accentColor: '#10b981' }}
                                />
                                {option.label}
                              </label>
                            ))}
                          </div>
                        ) : (
                          <input
                            type={field.type}
                            value={demoData[field.id] || ''}
                            readOnly
                            style={{
                              width: '100%',
                              padding: '0.75rem',
                              border: '1px solid #d1d5db',
                              borderRadius: '0.5rem',
                              fontSize: '0.875rem',
                              backgroundColor: demoData[field.id] ? '#f0fdf4' : 'white',
                              borderColor: demoData[field.id] ? '#10b981' : '#d1d5db'
                            }}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'download':
        return (
          <div style={{ textAlign: 'center' }}>
            <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#111827', marginBottom: '1rem' }}>
              Download Completed PDF
            </h3>
            <p style={{ color: '#6b7280', marginBottom: '2rem' }}>
              Generate and download the completed PDF with all your data.
            </p>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '2rem', alignItems: 'center', marginBottom: '3rem' }}>
              {/* Web Form */}
              <div style={{
                backgroundColor: '#dbeafe',
                border: '2px solid #2563eb',
                borderRadius: '0.5rem',
                padding: '1.5rem',
                position: 'relative'
              }}>
                <h4 style={{ fontSize: '1rem', fontWeight: 'bold', color: '#1e40af', marginBottom: '1rem' }}>
                  Completed Web Form
                </h4>
                <div style={{ fontSize: '0.75rem', color: '#1d4ed8' }}>
                  ✓ All fields filled<br/>
                  ✓ Data validated<br/>
                  ✓ Accessible format<br/>
                  ✓ Mobile optimized
                </div>
              </div>
              
              {/* Arrow */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
                <svg style={{ width: '2rem', height: '2rem', color: '#10b981' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
                <span style={{ fontSize: '0.75rem', color: '#10b981', fontWeight: '500' }}>Generate</span>
              </div>
              
              {/* Filled PDF */}
              <div style={{
                backgroundColor: '#dcfce7',
                border: '2px solid #10b981',
                borderRadius: '0.5rem',
                padding: '1.5rem',
                position: 'relative'
              }}>
                <h4 style={{ fontSize: '1rem', fontWeight: 'bold', color: '#166534', marginBottom: '1rem' }}>
                  Filled PDF
                </h4>
                <div style={{ fontSize: '0.75rem', color: '#14532d' }}>
                  ✓ Original format maintained<br/>
                  ✓ All data transferred<br/>
                  ✓ Ready for submission<br/>
                  ✓ Printable quality
                </div>
                
                <div style={{
                  position: 'absolute',
                  top: '-0.5rem',
                  right: '-0.5rem',
                  backgroundColor: '#10b981',
                  color: 'white',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '0.25rem',
                  fontSize: '0.75rem',
                  fontWeight: 'bold'
                }}>
                  PDF
                </div>
              </div>
            </div>
            
            <button
              onClick={() => alert('Demo PDF download would start here!')}
              style={{
                backgroundColor: '#10b981',
                color: 'white',
                padding: '0.75rem 2rem',
                border: 'none',
                borderRadius: '0.5rem',
                fontSize: '1rem',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                margin: '0 auto',
                marginBottom: '2rem'
              }}
            >
              <svg style={{ width: '1.25rem', height: '1.25rem' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Download Completed PDF
            </button>
            
            {/* Call to Action */}
            <div style={{
              backgroundColor: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '0.75rem',
              padding: '2rem',
              textAlign: 'center'
            }}>
              <h4 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#111827', marginBottom: '1rem' }}>
                Ready to try FormBridge?
              </h4>
              <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>
                Upload your own PDF or choose from our pre-made forms to get started.
              </p>
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                <Link to="/form" className="btn-primary">
                  Upload Your PDF
                </Link>
                <Link to="/" className="btn-secondary">
                  Learn More
                </Link>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div style={{ maxWidth: '64rem', margin: '0 auto', padding: '1rem' }}>
      {/* Progress Header */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '0.75rem',
        border: '1px solid #e5e7eb',
        padding: '1.5rem',
        marginBottom: '2rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#111827', margin: 0 }}>
            FormBridge Interactive Demo
          </h1>
          <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
            Step {currentStep + 1} of {steps.length}
          </span>
        </div>
        
        {/* Progress Bar */}
        <div style={{
          backgroundColor: '#e5e7eb',
          borderRadius: '9999px',
          height: '0.5rem',
          overflow: 'hidden',
          marginBottom: '1rem'
        }}>
          <div
            style={{
              backgroundColor: '#2563eb',
              height: '100%',
              borderRadius: '9999px',
              transition: 'width 0.5s ease',
              width: `${((currentStep + 1) / steps.length) * 100}%`
            }}
          />
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h2 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#111827', margin: 0, marginBottom: '0.25rem' }}>
              {steps[currentStep].title}
            </h2>
            <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: 0 }}>
              {steps[currentStep].description}
            </p>
          </div>
          
          {/* Navigation Buttons */}
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={prevStep}
              disabled={currentStep === 0}
              style={{
                padding: '0.5rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                backgroundColor: currentStep === 0 ? '#f3f4f6' : 'white',
                color: currentStep === 0 ? '#9ca3af' : '#374151',
                cursor: currentStep === 0 ? 'not-allowed' : 'pointer'
              }}
              aria-label="Previous step"
            >
              <svg style={{ width: '1rem', height: '1rem' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
            <button
              onClick={nextStep}
              disabled={currentStep === steps.length - 1}
              style={{
                padding: '0.5rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                backgroundColor: currentStep === steps.length - 1 ? '#f3f4f6' : 'white',
                color: currentStep === steps.length - 1 ? '#9ca3af' : '#374151',
                cursor: currentStep === steps.length - 1 ? 'not-allowed' : 'pointer'
              }}
              aria-label="Next step"
            >
              <svg style={{ width: '1rem', height: '1rem' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Step Content */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '0.75rem',
        border: '1px solid #e5e7eb',
        padding: '2rem',
        minHeight: '30rem',
        opacity: isAnimating ? 0.5 : 1,
        transition: 'opacity 0.3s ease'
      }}>
        {renderStepContent()}
      </div>

      {/* Add CSS for animations */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default DemoPage;